/**
 * Project List Page Facade
 * 
 * Unlike the LandingPageFacade which only shows active projects,
 * the ProjectListPage needs ALL projects (active AND archived)
 * so the template can display them in separate sections.
 * 
 * Usage (Web):
 * ```tsx
 * const navigation = useWebNavigation();
 * const auth = useAuthDialog();
 * const facade = useProjectListPageFacade({ navigation, auth, showToast });
 * ```
 * 
 * Usage (Plugin):
 * ```tsx
 * const navigation = usePluginNavigation();
 * const auth = usePairingCodeAuth();
 * const facade = useProjectListPageFacade({ navigation, auth, showToast });
 * ```
 * 
 * @module useProjectListPageFacade
 * @category Facades/Pages
 */

import { useMemo } from 'react';
import { 
  useLandingPageFacade, 
  type LandingPageFacade, 
  type LandingPageFacadeParams 
} from './useLandingPageFacade';
import { useProjectFacade } from '../shared/useProjectFacade';

/**
 * Project List Page Facade Params
 * Identical to LandingPageFacadeParams
 */
export type ProjectListPageFacadeParams = LandingPageFacadeParams;

/**
 * Project List Page Facade
 * Identical to LandingPageFacade
 */
export type ProjectListPageFacade = LandingPageFacade;

/**
 * Project List Page Facade Hook
 * 
 * Returns ALL projects (active + archived) so the template can
 * display them in separate sections.
 * 
 * @param params - Navigation, Auth, and ShowToast dependencies
 * @returns Composed facade with all project list page logic
 */
export function useProjectListPageFacade(
  params: ProjectListPageFacadeParams
): ProjectListPageFacade {
  // Get the base facade (which has filtered active projects)
  const baseFacade = useLandingPageFacade(params);
  
  // Get ALL projects (not filtered) from project facade
  const project = useProjectFacade();
  
  // Return facade with ALL projects (not filtered)
  // The template will handle separating active and archived
  return useMemo(() => ({
    ...baseFacade,
    state: {
      ...baseFacade.state,
      myProjects: project.projects, // All projects, not filtered
    },
  }), [baseFacade, project.projects]);
}
